import type {
  CandidateProfile,
  EmployerProfile,
  JobListing,
  JobMatch,
  Province,
  WorkType,
} from '@/types';

const STORAGE_KEY = 'bluepages:store:v1';

export const PROVINCES: Province[] = [
  'Gauteng',
  'Western Cape',
  'KwaZulu-Natal',
  'Eastern Cape',
  'Free State',
  'Limpopo',
  'Mpumalanga',
  'North West',
  'Northern Cape',
];

export const SKILL_CATALOG: Record<string, string[]> = {
  Construction: ['Bricklaying', 'Plastering', 'Carpentry', 'Tiling', 'Painting', 'Roofing', 'Plumbing', 'Welding', 'Drywalling', 'Paving'],
  Electrical: ['Electrical', 'Solar Installation', 'Generator Repair', 'Circuit Testing', 'DB Board Installation', 'Appliance Repair'],
  Maintenance: ['Handyman', 'Property Maintenance', 'Garden Services', 'Pool Maintenance', 'Gate Motor Repair', 'Geyser Installation'],
  Automotive: ['Mechanic', 'Panel Beating', 'Car Audio', 'Tyre Fitting', 'Auto Electrician'],
  IT: ['Web Development', 'Network Support', 'Graphic Design', 'Data Capture', 'CCTV Installation', 'POS Installation'],
  Cleaning: ['Deep Cleaning', 'Carpet Cleaning', 'Office Cleaning', 'Window Cleaning', 'Pest Control'],
  Catering: ['Event Catering', 'Corporate Catering', 'Baking', 'Barista'],
  Landscaping: ['Landscaping', 'Tree Felling', 'Irrigation', 'Lawn Care'],
  Security: ['Armed Response', 'CCTV Monitoring', 'Access Control', 'Guarding'],
  Beauty: ['Hair Styling', 'Nail Technician', 'Makeup', 'Massage Therapy'],
};

export const ALL_SKILLS: string[] = Object.values(SKILL_CATALOG).flat();

export const AVAILABILITY = ['Immediate', 'TwoWeeks', 'OneMonth', 'Contract', 'PerNegotiation'] as const;

export interface SeedCandidate extends CandidateProfile {
  name: string;
  skills: Array<{ name: string; category: string; proficiency: 'Beginner' | 'Intermediate' | 'Expert' }>;
  experience: Array<{ company: string; title: string; start: string; end?: string; description: string }>;
  insurance: { coidaRegistered: boolean; uifRegistered: boolean; verified: boolean };
  endorsements: number;
  yearsExperience: number;
}

export interface SeedJob extends JobListing {
  employerName: string;
  postedAt: string;
  skillsRequired: string[];
}

const CITIES: Record<Province, string[]> = {
  Gauteng: ['Johannesburg', 'Pretoria', 'Centurion', 'Sandton', 'Soweto'],
  'Western Cape': ['Cape Town', 'Stellenbosch', 'Paarl', 'George'],
  'KwaZulu-Natal': ['Durban', 'Pietermaritzburg', 'Umhlanga'],
  'Eastern Cape': ['Port Elizabeth', 'East London', 'Mthatha'],
  'Free State': ['Bloemfontein', 'Welkom'],
  Limpopo: ['Polokwane', 'Tzaneen'],
  Mpumalanga: ['Nelspruit', 'Secunda', 'Witbank'],
  'North West': ['Mahikeng', 'Rustenburg'],
  'Northern Cape': ['Kimberley', 'Upington'],
};

const FIRST_NAMES = ['Sipho', 'Thabo', 'Naledi', 'Johan', 'Maria', 'Pieter', 'Zanele', 'David', 'Lerato', 'Andre', 'Nomsa', 'Kagiso', 'Buhle', 'Chris', 'Anele', 'Sifiso', 'Megan', 'Teboho', 'Riyaadh', 'Yolanda'];
const LAST_NAMES = ['Dlamini', 'van Wyk', 'Petersen', 'Nkosi', 'Botha', 'Mokoena', 'Naidoo', 'Mahlangu', 'de Villiers', 'Zulu', 'Sithole', 'Coetzee', 'Khumalo', 'du Plessis', 'Mthembu', 'Molefe', 'Hendricks', 'Mtshali', 'Khan', 'Ferreira'];

const COMPANIES = ['BuildCo SA', 'FixIt Pros', 'Sparkie Electrical', 'FlowMaster Plumbing', 'PaintCraft', 'WeldWorks', 'GardenGuru', 'SecureGuard', 'CleanSweep', 'ByteTech Solutions', 'CaterKing', 'RoofRight', 'TileTech', 'HandyHub', 'SolarSouth'];

function pick<T>(arr: T[], i: number): T {
  return arr[i % arr.length];
}

function makeCandidates(): SeedCandidate[] {
  const out: SeedCandidate[] = [];
  for (let i = 0; i < 24; i++) {
    const province = pick(PROVINCES, i * 3);
    const cities = CITIES[province];
    const city = pick(cities, i * 2);
    const profileType = i % 7 === 0 ? 'Organisation' : 'Individual';
    const first = pick(FIRST_NAMES, i);
    const last = pick(LAST_NAMES, i);
    const name = profileType === 'Organisation' ? `${pick(COMPANIES, i)} Crew` : `${first} ${last}`;
    const cats = Object.keys(SKILL_CATALOG);
    const cat = pick(cats, i);
    const catSkills = SKILL_CATALOG[cat];
    const skillCount = 3 + (i % 4);
    const skills = Array.from({ length: skillCount }, (_, s) => {
      const sk = pick(catSkills, i + s);
      return { name: sk, category: cat, proficiency: (['Beginner', 'Intermediate', 'Expert'] as const)[(i + s) % 3] };
    });
    const availability = pick([...AVAILABILITY], i);
    const workType = pick<WorkType>(['Contract', 'Permanent', 'PartTime', 'Freelance', 'Any'], i);
    const slug = `${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}-${i.toString(36)}`;
    const id = `cand_${i.toString(36)}`;
    out.push({
      _id: id,
      type: 'candidateprofile',
      urn: `urn:rosta:workseeking:candidateprofile:${id}`,
      tenant: { type: 'tenant', id },
      legalEntity: { type: 'legalentity', id },
      profileType,
      slug,
      name,
      headline: `${pick(['Senior', 'Certified', 'Experienced', 'Qualified', 'Reliable'], i)} ${pick(catSkills, i)}`,
      bio: `${name} is a ${profileType === 'Organisation' ? 'crew' : 'tradesperson'} specialising in ${cat.toLowerCase()} work across ${city}, ${province}. Known for quality workmanship and punctual delivery.`,
      availability,
      workType,
      desiredLocations: [{ label: city }, { label: province }],
      desiredSalaryMin: 8000 + (i % 5) * 3000,
      desiredSalaryMax: 18000 + (i % 6) * 4000,
      isMarketplaceVisible: true,
      searchSyncStatus: 'Synced',
      status: 'Active',
      metadata: { createdAt: new Date().toISOString(), createdBy: id, updatedAt: new Date().toISOString(), updatedBy: id },
      skills,
      experience: [
        { company: pick(COMPANIES, i), title: pick(catSkills, i), start: '2021-01', description: `Delivered ${cat.toLowerCase()} projects for residential and commercial clients.` },
        { company: pick(COMPANIES, i + 1), title: pick(catSkills, i + 1), start: '2018-03', end: '2020-12', description: 'Site supervision and hands-on trade work.' },
      ],
      insurance: {
        coidaRegistered: i % 2 === 0,
        uifRegistered: i % 3 !== 0,
        verified: i % 4 === 0,
      },
      endorsements: (i * 3) % 40,
      yearsExperience: 2 + (i % 12),
    });
  }
  return out;
}

function makeEmployers(): EmployerProfile[] {
  return COMPANIES.map((c, i) => ({
    _id: `emp_${i.toString(36)}`,
    type: 'employerprofile',
    urn: `urn:rosta:workseeking:employerprofile:emp_${i}`,
    tenant: { type: 'tenant', id: `emp_${i}` },
    legalEntity: { type: 'legalentity', id: `emp_${i}` },
    companyName: c,
    industry: pick(['Construction', 'Property', 'Retail', 'Hospitality', 'Tech'], i),
    companySize: pick(['1-10', '11-50', '51-200', '201-500', '500+'] as const, i),
    description: `${c} connects clients with vetted tradespeople across South Africa.`,
    isVerified: i % 2 === 0,
    status: 'Active',
    metadata: { createdAt: new Date().toISOString(), createdBy: 'seed', updatedAt: new Date().toISOString(), updatedBy: 'seed' },
  }));
}

function makeJobs(employers: EmployerProfile[]): SeedJob[] {
  const out: SeedJob[] = [];
  for (let i = 0; i < 18; i++) {
    const emp = pick(employers, i);
    const province = pick(PROVINCES, i * 2);
    const city = pick(CITIES[province], i);
    const cats = Object.keys(SKILL_CATALOG);
    const cat = pick(cats, i);
    const skill = pick(SKILL_CATALOG[cat], i);
    const id = `job_${i.toString(36)}`;
    const daysAgo = i % 10;
    out.push({
      _id: id,
      type: 'joblisting',
      urn: `urn:rosta:workseeking:joblisting:${id}`,
      tenant: emp.tenant,
      employerProfile: { type: 'employerprofile', id: emp._id },
      title: `${skill} — ${pick(['Contract', 'Project', 'Callout', 'Full-time', 'Part-time'], i)}`,
      description: `${emp.companyName} is looking for an experienced ${skill.toLowerCase()} in ${city}. ${pick(['Immediate start.', 'Flexible hours.', 'Project-based engagement.', 'Ongoing contract work.'], i)}`,
      requirements: [`Proven ${skill.toLowerCase()} experience`, 'Own tools', 'Reliable transport'],
      skillsRequired: [skill, ...Array.from({ length: i % 2 }, () => pick(SKILL_CATALOG[cat], i + 1))],
      workType: pick(['Contract', 'Permanent', 'PartTime', 'Freelance'] as const, i),
      location: city,
      province,
      salaryMin: 6000 + (i % 5) * 2000,
      salaryMax: 15000 + (i % 6) * 3000,
      applicationDeadline: new Date(Date.now() + (10 + i) * 86400000).toISOString().slice(0, 10),
      status: 'Open',
      postedAt: new Date(Date.now() - daysAgo * 86400000).toISOString(),
      employerName: emp.companyName,
      searchSyncStatus: 'Synced',
      metadata: { createdAt: new Date().toISOString(), createdBy: 'seed', updatedAt: new Date().toISOString(), updatedBy: 'seed' },
    });
  }
  return out;
}

export interface StoreData {
  candidates: SeedCandidate[];
  employers: EmployerProfile[];
  jobs: SeedJob[];
  matches: JobMatch[];
}

function seed(): StoreData {
  const candidates = makeCandidates();
  const employers = makeEmployers();
  const jobs = makeJobs(employers);
  return { candidates, employers, jobs, matches: [] };
}

let cache: StoreData | null = null;

export function loadStore(): StoreData {
  if (cache) return cache;
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (raw) {
      cache = JSON.parse(raw) as StoreData;
      return cache;
    }
  } catch {
    /* ignore */
  }
  cache = seed();
  persist();
  return cache;
}

export function persist(): void {
  if (!cache) return;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    /* ignore */
  }
}

export function resetStore(): void {
  cache = seed();
  persist();
}

export function getCandidate(slug: string): SeedCandidate | undefined {
  return loadStore().candidates.find((c) => c.slug === slug || c._id === slug);
}

export function getJob(id: string): SeedJob | undefined {
  return loadStore().jobs.find((j) => j._id === id);
}

export function getEmployer(id: string): EmployerProfile | undefined {
  return loadStore().employers.find((e) => e._id === id);
}

export interface CandidateFilters {
  query?: string;
  ofoCode?: string;
  skills?: string[];
  categories?: string[];
  availability?: string;
  workType?: string;
  province?: string;
  city?: string;
  coida?: boolean;
  uif?: boolean;
  salaryMin?: number;
  salaryMax?: number;
  h3Origin?: string;
  radiusKm?: number;
}

export function searchCandidates(f: CandidateFilters = {}): SeedCandidate[] {
  // Bare search (no term, no location) must return nothing — never the whole store.
  if (!f.query && !f.province && !f.city) return [];
  const all = loadStore().candidates;
  return all.filter((c) => {
    if (f.query) {
      const q = f.query.toLowerCase();
      const hay = `${c.name} ${c.headline} ${c.skills.map((s) => s.name).join(' ')}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (f.skills?.length && !f.skills.every((s) => c.skills.some((cs) => cs.name === s))) return false;
    if (f.categories?.length && !f.categories.some((cat) => c.skills.some((s) => s.category === cat))) return false;
    if (f.availability && c.availability !== f.availability) return false;
    if (f.workType && f.workType !== 'Any' && c.workType !== f.workType) return false;
    if (f.province && !c.desiredLocations?.some((d) => d.label === f.province)) return false;
    if (f.city && !c.desiredLocations?.some((d) => d.label === f.city)) return false;
    if (f.coida && !c.insurance.coidaRegistered) return false;
    if (f.uif && !c.insurance.uifRegistered) return false;
    if (f.salaryMin && (c.desiredSalaryMax ?? 0) < f.salaryMin) return false;
    if (f.salaryMax && (c.desiredSalaryMin ?? 0) > f.salaryMax) return false;
    return true;
  });
}

export function searchJobs(f: CandidateFilters = {}): SeedJob[] {
  // Bare search (no term, no location) must return nothing — never the whole store.
  if (!f.query && !f.province && !f.city) return [];
  const all = loadStore().jobs;
  return all.filter((j) => {
    if (f.query) {
      const q = f.query.toLowerCase();
      const hay = `${j.title} ${j.description} ${j.skillsRequired.join(' ')} ${j.employerName}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (f.skills?.length && !f.skills.some((s) => j.skillsRequired.includes(s))) return false;
    if (f.province && j.province !== f.province) return false;
    if (f.city && j.location !== f.city) return false;
    if (f.workType && f.workType !== 'Any' && j.workType !== f.workType) return false;
    if (f.salaryMin && (j.salaryMax ?? 0) < f.salaryMin) return false;
    if (f.salaryMax && (j.salaryMin ?? 0) > f.salaryMax) return false;
    return true;
  });
}

export function createJob(input: Partial<SeedJob> & { employerName: string }): SeedJob {
  const s = loadStore();
  const id = `job_${Date.now().toString(36)}`;
  const job: SeedJob = {
    _id: id,
    type: 'joblisting',
    urn: `urn:rosta:workseeking:joblisting:${id}`,
    tenant: { type: 'tenant', id: 'self' },
    employerProfile: { type: 'employerprofile', id: 'self' },
    title: input.title ?? 'Untitled Job',
    description: input.description ?? '',
    requirements: input.requirements ?? [],
    skillsRequired: input.skillsRequired ?? [],
    workType: (input.workType as 'Contract' | 'Permanent' | 'PartTime' | 'Freelance') ?? 'Contract',
    location: input.location ?? 'Johannesburg',
    province: input.province ?? 'Gauteng',
    salaryMin: input.salaryMin,
    salaryMax: input.salaryMax,
    applicationDeadline: input.applicationDeadline,
    status: 'Open',
    postedAt: new Date().toISOString(),
    employerName: input.employerName,
    searchSyncStatus: 'Pending',
    metadata: { createdAt: new Date().toISOString(), createdBy: 'self', updatedAt: new Date().toISOString(), updatedBy: 'self' },
  };
  s.jobs.unshift(job);
  persist();
  return job;
}

export function suggestMatches(candidateSlug: string): JobMatch[] {
  const c = getCandidate(candidateSlug);
  if (!c) return [];
  const s = loadStore();
  const candSkills = new Set(c.skills.map((sk) => sk.name));
  const matches = s.jobs
    .map((j) => {
      const matched = j.skillsRequired.filter((sk) => candSkills.has(sk)).length;
      const score = Math.min(99, 40 + matched * 25 + (c.desiredLocations?.some((d) => d.label === j.province) ? 10 : 0));
      return {
        _id: `match_${c._id}_${j._id}`,
        type: 'jobmatch' as const,
        urn: `urn:rosta:workseeking:jobmatch:match_${c._id}_${j._id}`,
        jobListing: { type: 'joblisting' as const, id: j._id },
        candidateProfile: { type: 'candidateprofile' as const, id: c._id },
        matchScore: score,
        matchReason: matched > 0 ? `${matched} skill${matched > 1 ? 's' : ''} match · ${j.province}` : `Based in ${j.province}`,
        employerViewed: false,
        candidateViewed: false,
        status: 'Suggested' as const,
        metadata: { createdAt: new Date().toISOString(), createdBy: 'self', updatedAt: new Date().toISOString(), updatedBy: 'self' },
      };
    })
    .sort((a, b) => (b.matchScore ?? 0) - (a.matchScore ?? 0))
    .slice(0, 5);
  return matches;
}

export function relatedCandidates(slug: string): SeedCandidate[] {
  const c = getCandidate(slug);
  if (!c) return [];
  const cat = c.skills[0]?.category;
  return loadStore()
    .candidates.filter((x) => x.slug !== slug && x.skills.some((s) => s.category === cat))
    .slice(0, 8);
}
